import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Attachment } from 'app/edozvola/cases/cases.model';
import { CodebookModel } from 'app/shared/model/shared.model';
import { useDispatch } from 'react-redux';
import { fetchAttachment, saveAttachment, saveFile } from 'app/edozvola/cases/service/cases.service';
import { fetchAttachmentTypeCodebook } from 'app/edozvola/codebooks/codebooks.service';
import { Field, Form, Formik, getIn, useFormikContext } from 'formik';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, FormGroup, Row } from 'reactstrap';
import { Button, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { FormikPredictiveInput } from 'edozvola-frontend-lib/dist/ui/components/predictive-input/FormikPredictiveInput';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { BYTES_IN_A_MEGABYTE, ERRORS, FILE_SIZE_LIMIT_IN_MEGABYTES, messages } from 'app/config/constants';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadFileFromBase64 } from 'app/shared/util/file-utils';
import axios from 'axios';
import './request-attachments.scss';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import {updateCaseRequestAttachmentMutation} from "app/edozvola/cases/service/cases.queries";

const CreateNewAttachment = () => {
  const { id } = useParams<{ id: string }>();
  const { idAttachment } = useParams<{ idAttachment: string }>();
  const [initialData, setInitialData] = useState<Attachment>();
  const [attachmentTypeOptions, setAttachmentTypeOptions] = useState<CodebookModel[]>([]);
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hiddenFileInput = React.useRef(null);
  const {mutate: saveMutation, isPending: isSaving} = updateCaseRequestAttachmentMutation(id, idAttachment, data => {
    if (initialData.idCaseAttachment === null) {
      navigate(`/cases/${id}/attachments/${data.idCaseAttachment}`);
    }
    setInitialData(data);
    showToast(toast.success, messages.DATA_SAVED);
  }, error => {
    if (Object.values(ERRORS).includes(error.data.message)) {
      showToast(toast.error, error.data.message);
    } else {
      showToast(toast.error, ERRORS.GENERIC_ERROR);
    }
  });

  useEffect(() => {
    setLoading(true);

    fetchCodebooks();

    fetchAttachment(id, Number(idAttachment)).then(attachment => {
      setInitialData(attachment);
      setLoading(false);
    });
  }, []);

  const fetchCodebooks = () => {
    fetchAttachmentTypeCodebook().then(data => setAttachmentTypeOptions(data));
  };

  const handleSubmit = (values, { setSubmitting }) => {
    saveMutation(values);
  };

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  const validation = Yup.object({
    name: Yup.string().required(messages.MANDATORY_FIELD),
  });

  const selectFile = (values: any) => {
    if (values.document && values.document?.fileName) {
      axios
        .get(`/api/cases/${id}/attachments/${initialData.idCaseAttachment}/preview-file`)
        .then(response => {
          const data = response.data;
          downloadFileFromBase64(initialData?.document?.fileName, data.contentData, data.fileType.type);
        })
        .catch(() => {
          showToast(toast.error, messages.FETCHING_DOCUMENT_ERROR);
        });
    } else {
      hiddenFileInput.current.click();
    }
  };

  const appendFile = async (event, setFieldValue) => {
    if (event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > FILE_SIZE_LIMIT_IN_MEGABYTES * BYTES_IN_A_MEGABYTE) {
        showToast(toast.error, messages.DOCUMENT_SIZE_LIMIT);
        return;
      } else if (file.type !== 'application/pdf') {
        showToast(toast.error, messages.DOCUMENT_PDF_FORMAT);
        return;
      }

      saveFile(id, idAttachment, file).then(value => {
        setFieldValue('document', value);
      });
    }
  };

  const removeFile = setFieldValue => {
    hiddenFileInput.current.value = null;
    setFieldValue('document', null);
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        initialValues={initialData}
        isInitialValid={false}
        validationSchema={validation}
        onSubmit={handleSubmit}
      >
        {({ errors, isValid, dirty, resetForm, submitForm, setValues, setFieldValue, setTouched, values }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <Row>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Vrsta priloga'} />
                  <Field
                    id="type"
                    name="type"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={'Odaberi vrstu priloga'}
                        onItemSelect={selectedOption => setFieldValue('type', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={attachmentTypeOptions}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Label labelText={'Naziv privitka'} />
                  <Field id="name" name="name" render={({ field }) => <Input {...field} type="input" error={getIn(errors, 'name')} />} />
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup>
                  <Input
                    placeholder={'Priloži dokument'}
                    addonPrepend={
                      values?.document?.fileName ? (
                        <FontAwesomeIcon icon={faTimes} onClick={event => removeFile(setFieldValue)} className="hand" />
                      ) : null
                    }
                    name="document.fileName"
                    value={values?.document?.fileName || ''}
                    onClick={() => selectFile(values)}
                    readOnly
                    disabled={Number(idAttachment) === 0}
                    className={`readonly-upload-input ${Number(idAttachment) === 0 ? 'disabled-input' : ''}`}
                    addonAppend={
                      <FontAwesomeIcon
                        icon={'file-upload'}
                        onClick={() => selectFile(values)}
                        className={`hand ${Number(idAttachment) === 0 ? 'disabled-input' : ''}`}
                      />
                    }
                  />
                  <input
                    type="file"
                    ref={hiddenFileInput}
                    accept="application/pdf"
                    disabled={Number(idAttachment) === 0}
                    onChange={event => {
                      appendFile(event, setFieldValue);
                    }}
                    className="hidden-upload"
                  />
                  {values?.document?.idAttachment === 0 && (
                    <span className="info-text">* Prije učitavanja dokumenta, Potrebno je prvo spremiti formu!</span>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <div className="case-footer-relative">
              <Button color={'info'} label={'Spremi'} isLoading={isSaving} size={'lg'} onClick={submitForm} disabled={!dirty || !isValid} />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateNewAttachment;
